"use client";

import {
	type DefaultImageProps,
	type DefaultLinkProps,
	Heading,
	type HeadingProps,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	Price,
	type PriceProps,
	Rating,
	type RatingProps,
	RichText
} from "@pamperedchef/design-system";

import * as styles from "./ReplacementPart.module.scss";

interface ReplacementPartProps<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> {
	heading: HeadingProps;
	description: string;
	subheading: HeadingProps;
	info: string;
	product: {
		image: ImageProps<ImageComponentProps>;
		price?: PriceProps;
		rating?: RatingProps;
		cta: LinkProps<LinkComponentProps>;
	};
}

/**
 * Replacement Part Component
 */
export function ReplacementPart<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
>(
	props: ReplacementPartProps<ImageComponentProps, LinkComponentProps>
): JSX.Element {
	return (
		<div className={styles["replacement-part"]}>
			<Heading
				{...props.heading}
				className={styles["heading"]}
				styledAs="h2"
			/>
			<RichText
				className={styles["description"]}
				content={props.description}
			/>
			<article className={styles["card"]}>
				<div className={styles["card-content"]}>
					<Heading
						{...props.subheading}
						className={styles["subheading"]}
						styledAs="h2"
					/>
					<RichText className={styles["info"]} content={props.info} />
				</div>
				<div className={styles["product"]}>
					<Image
						{...props.product.image}
						className={styles["image"]}
						alt={props.product.image.alt || ""}
					/>
					<div className={styles["content"]}>
						<Link
							{...props.product.cta}
							kind="tertiary"
							className={styles["cta"]}
						/>
						{props.product.rating && (
							<Rating {...props.product.rating} size="small" />
						)}
						{props.product.price && (
							<Price {...props.product.price} />
						)}
					</div>
				</div>
			</article>
		</div>
	);
}
